// src/components/handover/FileUploadWithPreview.jsx
import React from 'react';
import { Button } from '../../ui';
import { labelStyle } from '../../handover/handoverStyles';
import {
  createPendingUpload,
  startUpload,
  removeUploadedFile,
  isCancelError,
} from '../../handover/handoverUtils';

export default function FileUploadWithPreview({
  label,
  uploads,
  onUploadsChange,
  uploadPrefix,
  reservationId,
  multiple = true,
  accept,
  disabled = false,
  preserveUploadedOnUnmount = false,
}) {
  const inputRef = React.useRef(null);
  const tasksRef = React.useRef(new Map());
  const itemsRef = React.useRef([]);
  const preserveRef = React.useRef(preserveUploadedOnUnmount);
  const previousItemsRef = React.useRef([]);

  const items = React.useMemo(() => (Array.isArray(uploads) ? uploads : []), [uploads]);

  React.useEffect(() => {
    itemsRef.current = items;
    preserveRef.current = preserveUploadedOnUnmount;
  }, [items, preserveUploadedOnUnmount]);

  React.useEffect(() => {
    const previousItems = previousItemsRef.current;
    const nextIds = new Set(items.map((item) => item?.id));

    previousItems.forEach((item) => {
      if (item?.previewUrl && item?.id && !nextIds.has(item.id)) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {
          // ignore
        }
      }
    });

    previousItemsRef.current = items;
  }, [items]);

  React.useEffect(() => () => {
    Array.from(tasksRef.current.values()).forEach((entry) => {
      try {
        entry?.task?.cancel?.('Upload cancelled');
      } catch {
        // ignore
      }
    });
    tasksRef.current.clear();

    const finalItems = itemsRef.current || [];
    finalItems.forEach((item) => {
      if (item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {
          // ignore
        }
      }
    });

    if (preserveRef.current) return;

    finalItems.forEach((item) => {
      if (item?.status === 'uploaded' && item?.key) {
        removeUploadedFile(item.key).catch(() => {});
      }
    });
  }, []);

  function mutateItems(updater) {
    if (typeof onUploadsChange !== 'function') return;
    onUploadsChange((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return updater(current);
    });
  }

  function updateItem(id, patch) {
    mutateItems((current) =>
      current.map((item) => (item?.id === id ? { ...item, ...patch } : item))
    );
  }

  function removeItem(id) {
    mutateItems((current) => current.filter((item) => item?.id !== id));
  }

  function beginUpload(item) {
    const file = item?.file;
    if (!file || !uploadPrefix || !reservationId) {
      updateItem(item.id, {
        status: 'error',
        error: 'Missing upload destination.',
      });
      return;
    }

    updateItem(item.id, {
      status: 'uploading',
      progress: 0,
      error: '',
    });

    const { key, task } = startUpload({
      prefix: uploadPrefix,
      reservationId,
      file,
      onProgress: ({ transferredBytes, totalBytes }) => {
        const nextProgress =
          totalBytes && totalBytes > 0
            ? Math.max(1, Math.min(100, Math.round((transferredBytes / totalBytes) * 100)))
            : transferredBytes > 0
            ? 1
            : 0;

        updateItem(item.id, {
          status: 'uploading',
          progress: nextProgress,
        });
      },
    });

    tasksRef.current.set(item.id, { task, key });

    task.result
      .then(() => {
        tasksRef.current.delete(item.id);
        updateItem(item.id, {
          key,
          status: 'uploaded',
          progress: 100,
          error: '',
          file: null,
        });
      })
      .catch((error) => {
        tasksRef.current.delete(item.id);
        if (isCancelError(error)) {
          removeItem(item.id);
          return;
        }

        updateItem(item.id, {
          key: null,
          status: 'error',
          error: error?.message || 'Upload failed.',
        });
      });
  }

  function handleChange(e) {
    if (disabled) {
      e.target.value = '';
      return;
    }

    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const existingFingerprints = new Set(
      items.map((item) => item?.fingerprint).filter(Boolean)
    );
    const nextItems = [];

    for (const file of selected) {
      const pending = createPendingUpload(file);
      if (existingFingerprints.has(pending.fingerprint)) {
        try {
          URL.revokeObjectURL(pending.previewUrl);
        } catch {
          // ignore
        }
        continue;
      }
      existingFingerprints.add(pending.fingerprint);
      nextItems.push(pending);
    }

    if (!nextItems.length) {
      e.target.value = '';
      return;
    }

    mutateItems((current) => [...current, ...nextItems]);
    nextItems.forEach(beginUpload);
    e.target.value = '';
  }

  function handleRemove(item) {
    if (disabled || !item?.id) return;

    const active = tasksRef.current.get(item.id);
    if (active?.task) {
      try {
        active.task.cancel('Upload removed');
      } catch {
        removeItem(item.id);
      }
      return;
    }

    if (item.key) {
      updateItem(item.id, {
        status: 'removing',
        error: '',
      });

      removeUploadedFile(item.key)
        .then(() => {
          removeItem(item.id);
        })
        .catch((error) => {
          updateItem(item.id, {
            status: 'error',
            error: error?.message || 'Failed to delete uploaded file.',
          });
        });
      return;
    }

    removeItem(item.id);
  }

  function openPicker() {
    if (disabled) return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      {label && <span style={labelStyle}>{label}</span>}

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={handleChange}
      />

      <div style={{ marginTop: 4, marginBottom: 4 }}>
        <Button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            borderRadius: 999,
            background: '#111827',
            color: '#f9fafb',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          + Upload {multiple ? 'photos' : 'file'}
        </Button>
        <span
          style={{
            marginLeft: 8,
            fontSize: 11,
            color: '#6b7280',
          }}
        >
          JPG / PNG accepted. Upload starts immediately.
        </span>
      </div>

      {items.length > 0 && (
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {items.map((item) => {
            const status = String(item?.status || 'queued');
            const progress = Math.max(0, Math.min(100, Number(item?.progress || 0)));
            const canRemove = !disabled && status !== 'removing';
            const statusTone =
              status === 'uploaded'
                ? '#065f46'
                : status === 'error'
                ? '#991b1b'
                : status === 'removing'
                ? '#92400e'
                : '#1d4ed8';
            const statusLabel =
              status === 'uploaded'
                ? 'Uploaded'
                : status === 'error'
                ? 'Failed'
                : status === 'removing'
                ? 'Removing...'
                : `Uploading ${progress}%`;

            return (
              <div
                key={item.id}
                style={{
                  width: 90,
                  fontSize: 10,
                  color: '#6b7280',
                  position: 'relative',
                }}
              >
                <img
                  src={item.previewUrl}
                  alt={item.name}
                  style={{
                    width: '100%',
                    height: 70,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleRemove(item)}
                  disabled={!canRemove}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 18,
                    height: 18,
                    borderRadius: '999px',
                    border: 'none',
                    background: 'rgba(15,23,42,0.85)',
                    color: '#f9fafb',
                    fontSize: 11,
                    cursor: canRemove ? 'pointer' : 'default',
                    lineHeight: 1,
                    opacity: canRemove ? 1 : 0.6,
                  }}
                >
                  x
                </button>
                {(status === 'uploading' || status === 'removing') && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 4,
                      right: 4,
                      bottom: 20,
                      height: 6,
                      background: 'rgba(255,255,255,0.75)',
                      borderRadius: 999,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: status === 'removing' ? '#f59e0b' : '#2563eb',
                      }}
                    />
                  </div>
                )}
                <div
                  style={{
                    marginTop: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={item.name}
                >
                  {item.name}
                </div>
                <div style={{ marginTop: 2, color: statusTone }} title={item.error || statusLabel}>
                  {item.error || statusLabel}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
